import { safeJSONParse } from 'utils/data/parse.utils';

/**
 * Alert providers often send the same information under different field names.
 * This file gives the rest of the application one consistent way to read that data.
 */

/**
 * Finds the first field value that is not null, undefined, or an empty string.
 *
 * The provider-specific readers below use this helper to express field preference clearly.
 * For example, a Custom Weather headline is preferred before the generic description field.
 *
 * @param {unknown[]} fieldValues - Values listed from most preferred to least preferred.
 * @param {unknown} [fallback=null] - Value returned when every field is empty.
 * @returns {unknown} The first populated value, or the fallback.
 */
const findFirstPopulatedFieldValue = (fieldValues, fallback = null) => {
  return (
    fieldValues.find((value) => value !== null && value !== undefined && value !== '') ?? fallback
  );
};

/**
 * Reads weather-zone IDs from one field supplied by an alert provider.
 *
 * A weather-zone ID identifies a predefined warning area, such as an NWS UGC zone. Providers
 * do not agree on a format: the field may be an array, JSON text, a number, or comma-separated
 * text. This helper turns every supported format into an array so boundary lookup code does not
 * need provider-specific branches.
 *
 * @param {unknown} weatherZoneFieldValue - One raw zone field from an alert response.
 * @returns {Array<string|number>} Weather-zone IDs found in that field.
 */
const getWeatherZoneIdsFromProviderField = (weatherZoneFieldValue) => {
  if (Array.isArray(weatherZoneFieldValue)) return weatherZoneFieldValue.flat(Infinity);
  if (typeof weatherZoneFieldValue === 'number') return [weatherZoneFieldValue];
  if (typeof weatherZoneFieldValue !== 'string') return [];

  const parsedJson = safeJSONParse(weatherZoneFieldValue, null);
  if (Array.isArray(parsedJson)) return parsedJson.flat(Infinity);

  return weatherZoneFieldValue.split(/[\s,]+/); // Split on whitespace or commas
};

/**
 * Returns the first ID supplied by any supported alert provider.
 *
 * Alert lists, Redux updates, and selection controls use this value to identify one alert. The
 * optional fallback is normally the alert's array index and is used only when the provider sent
 * no identifier.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @param {string|number} [fallbackIdentifier] - Last-resort identifier.
 * @returns {string|number|null} The provider ID, fallback ID, or null.
 */
export const getAlertIdentifier = (alert, fallbackIdentifier) => {
  return findFirstPopulatedFieldValue(
    [
      alert?.id,
      alert?.message_id,
      alert?.ca_identifier,
      alert?.cw_identifier,
      alert?.ga_identifier,
    ],
    fallbackIdentifier,
  );
};

/**
 * Returns the best short headline available for an alert.
 *
 * Detail panels and map popups use this value as the alert's primary title.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {string|number} The preferred headline or a readable fallback.
 */
export const getAlertHeadline = (alert) => {
  return findFirstPopulatedFieldValue(
    [alert?.smpp_message, alert?.headline, alert?.description, alert?.type],
    'No headline available',
  );
};

/**
 * Returns the best detailed message available for an alert.
 *
 * Alert detail views use this value for the explanatory body text.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {string} The preferred description or a readable fallback.
 */
export const getAlertDescription = (alert) => {
  return findFirstPopulatedFieldValue(
    [alert?.message, alert?.description],
    'No description available',
  );
};

/**
 * Returns the office, organization, or person that issued an alert.
 *
 * Detail cards use this value for their Sender field.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {string} The sender name or `N/A`.
 */
export const getAlertSender = (alert) => {
  return findFirstPopulatedFieldValue([alert?.nws_office, alert?.sender, alert?.madeBy], 'N/A');
};

/**
 * Returns the timestamp that best represents when an alert became active.
 *
 * The raw timestamp is intentionally preserved because the rendering component owns formatting
 * and timezone display.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {string|null} The source timestamp, or null when unavailable.
 */
export const getAlertStartTime = (alert) => {
  return findFirstPopulatedFieldValue([
    alert?.broadcast_time_utc,
    alert?.startTime,
    alert?.issue_time_utc,
    alert?.sentTime,
  ]);
};

/**
 * Returns the timestamp that best represents when an alert expires.
 *
 * The raw timestamp is intentionally preserved so callers can apply their own display format.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {string|null} The source timestamp, or null when unavailable.
 */
export const getAlertEndTime = (alert) => {
  return findFirstPopulatedFieldValue([alert?.valid_until_time_utc, alert?.endTime]);
};

/**
 * Returns an alert's severity as a usable number.
 *
 * Sorting and area styling use this value. Missing, blank, and non-numeric values stay null so
 * they are not accidentally treated as zero-severity alerts.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {number|null} The numeric severity score, or null.
 */
export const getAlertSeverity = (alert) => {
  const providerSeverity = alert?.severity;
  const isBlankString = typeof providerSeverity === 'string' && !providerSeverity.trim();
  if (providerSeverity === null || providerSeverity === undefined || isBlankString) return null;

  const numericSeverity = Number(providerSeverity);
  return Number.isFinite(numericSeverity) ? numericSeverity : null;
};

/**
 * Returns every unique weather-zone ID referenced by an alert.
 *
 * Providers currently use `ugc_codes`, `zoneCodes`, or `zone_codes` for the same concept. The
 * location utility uses these IDs to load the predefined polygons affected by the alert.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {string[]} Clean, unique weather-zone IDs.
 */
export const getAlertWeatherZoneIds = (alert) => {
  const providerWeatherZoneFieldValues = [alert?.ugc_codes, alert?.zoneCodes, alert?.zone_codes];
  const weatherZoneIds = providerWeatherZoneFieldValues
    .flatMap(getWeatherZoneIdsFromProviderField)
    .map((weatherZoneId) => String(weatherZoneId).trim())
    .filter(Boolean);

  return [...new Set(weatherZoneIds)];
};

/**
 * Selects the most severe alert from a collection.
 *
 * When several alerts cover the same weather zone, the map uses this alert for the area's color
 * and initial details. The first alert is retained when scores are tied or unavailable.
 *
 * @param {object[]} alerts - Alerts to compare.
 * @returns {object|null} The alert with the largest severity score, or null for an empty list.
 */
export const getHighestSeverityAlert = (alerts) => {
  if (!Array.isArray(alerts) || !alerts.length) return null;

  return alerts.reduce((highestSeverityAlert, currentAlert) => {
    const currentSeverity = getAlertSeverity(currentAlert) ?? -Infinity;
    const highestSeverity = getAlertSeverity(highestSeverityAlert) ?? -Infinity;
    return currentSeverity > highestSeverity ? currentAlert : highestSeverityAlert;
  });
};
